import React, { useEffect } from 'react';
import './Onboarding.css';
import type { ConnectorTabType } from './connector/connectorTabConfig';
import GuidedTourOverlay from './guidedTour/GuidedTourOverlay';
import type { GuidedTourStep } from './guidedTour/types';
import { useGuidedTour } from '../hooks/useGuidedTour';

interface ConnectorOnboardingStep extends GuidedTourStep {
  tab?: ConnectorTabType;
}

interface ConnectorOnboardingProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab: ConnectorTabType;
  onTabChange?: (tab: ConnectorTabType) => void;
  onSelectIntegration?: (integrationId: string) => void;
  selectedIntegrationId?: string | null;
  integrations?: any[];
}

const ConnectorOnboarding = ({ 
  isOpen, 
  onClose, 
  activeTab, 
  onTabChange,
  onSelectIntegration,
  selectedIntegrationId,
  integrations
}: ConnectorOnboardingProps) => {
  // Шаги для вкладки Integrations
  const integrationsSteps: ConnectorOnboardingStep[] = [
    {
      id: 'connector-page-header',
      title: 'Welcome to Connector',
      description: 'The Connector page helps you manage integrations, configure multi-agent systems, and optimize AI configurations. Let\'s explore the key features.',
      position: 'right',
      offset: { x: 60, y: 0 },
      tab: 'integrations'
    },
    {
      id: 'connector-tabs',
      title: 'Navigation Tabs',
      description: 'Switch between Integrations, Virtual Users, and Datasets tabs. Each tab provides different functionality for managing your AI systems.',
      position: 'bottom',
      offset: { x: 0, y: 20 },
      tab: 'integrations'
    },
    {
      id: 'create-integration-btn',
      title: 'Create Integration',
      description: 'Click here to create a new integration. You\'ll need to provide a URL and optionally an API key to connect external services.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'integrations'
    },
    {
      id: 'integrations-table',
      title: 'Integrations List',
      description: 'View all your integrations here. Click "Select" on an integration to configure it. Once selected, you\'ll see the Integration Configurator below. You can also use action buttons to edit, view details, copy ID, or delete integrations.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'integrations'
    },
    {
      id: 'connector-config-section',
      title: 'Integration Configurator',
      description: 'Here you can configure your multi-agent system. Set up the coordinator agent (master agent) and worker agents. For each agent, you can specify: name, prompt, AI model, temperature, and description. The coordinator coordinates workers and responds to users, while workers handle specific tasks. Use the buttons at the top to import, export, reset, or save your configuration.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'integrations'
    },
    {
      id: 'connector-optimize-run-btn',
      title: 'Optimize Configuration',
      description: 'After configuring your agents, pick a dataset and iterations, then use Optimize preview if you want a cost check, or Optimize to run the job. The main Optimize action is highlighted here.',
      position: 'top',
      offset: { x: 0, y: -30 },
      scrollIntoViewBlock: 'nearest',
      tab: 'integrations'
    }
  ];

  // Шаги для вкладки Virtual Users
  const virtualUsersSteps: ConnectorOnboardingStep[] = [
    {
      id: 'virtual-users-tab',
      title: 'Virtual Users Tab',
      description: 'The Virtual Users tab allows you to manage and test your integrations with simulated users. This helps you validate your AI configurations before deploying.',
      position: 'right',
      offset: { x: 60, y: 0 },
      tab: 'virtual-users'
    },
    {
      id: 'virtual-users-content',
      title: 'Virtual User Service',
      description: 'Here you can create and manage virtual users to test your integrations. Virtual users simulate real user interactions to help you validate your AI system behavior.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'virtual-users'
    },
    {
      id: 'vuser-create-btn',
      title: 'Create Virtual User',
      description: 'Click this button to create a new virtual user. You can configure the user type (user or admin), name, description, and AI configuration. Virtual users help you test how your AI system responds to different scenarios.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'virtual-users'
    },
    {
      id: 'vuser-table',
      title: 'Virtual Users Table',
      description: 'This table displays all your virtual users. You can see their names, types, assigned personas, and perform actions like editing, assigning personas, or deleting them.',
      position: 'top',
      offset: { x: 0, y: -20 },
      tab: 'virtual-users'
    },
    {
      id: 'vuser-personas-section',
      title: 'Personas Management',
      description: 'Personas define how virtual users react to AI responses. You can create personas with different reaction probabilities (like, dislike, comment) and assign them to virtual users. Select a virtual user first to manage their personas.',
      position: 'top',
      offset: { x: 0, y: -20 },
      tab: 'virtual-users'
    }
  ];

  // Шаги для вкладки Datasets
  const datasetsSteps: ConnectorOnboardingStep[] = [
    {
      id: 'datasets-tab',
      title: 'Datasets Tab',
      description: 'The Datasets tab lets you manage datasets used for evaluation and optimization. Upload, view, and manage your training and evaluation data here.',
      position: 'right',
      offset: { x: 60, y: 0 },
      tab: 'datasets'
    },
    {
      id: 'datasets-content',
      title: 'Dataset Management',
      description: 'Manage your datasets here. You can upload new datasets, view existing ones, and select datasets for optimization. Different datasets test different aspects of AI behavior.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'datasets'
    },
    {
      id: 'datasets-header',
      title: 'All Datasets',
      description: 'This section shows all your datasets. Each dataset represents an experiment that tests your AI system with virtual users. You can see the status, progress, and scores of each dataset.',
      position: 'bottom',
      offset: { x: 0, y: 20 },
      tab: 'datasets'
    },
    {
      id: 'datasets-start-new-btn',
      title: 'Start New Dataset',
      description: 'Click this button to start a new dataset experiment. You\'ll need to select virtual users, configure dialog count and messages per dialog. This creates a new experiment that tests your AI system.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'datasets'
    },
    {
      id: 'datasets-table',
      title: 'Datasets Table',
      description: 'This table displays all your datasets with their start dates, status (running/completed), progress, scores, and actions. Click "Details" to view detailed information about a dataset, or "Cancel" to stop a running experiment.',
      position: 'top',
      offset: { x: 0, y: -20 },
      tab: 'datasets'
    }
  ];

  const chatViewerSteps: ConnectorOnboardingStep[] = [
    {
      id: 'chat-viewer-tab',
      title: 'Chat Viewer',
      description: 'On this tab you review dialogs from the Virtual User service, inspect resolved labels, and edit human labels for final answers and individual assistant steps.',
      position: 'bottom',
      offset: { x: 0, y: 20 },
      tab: 'chat-viewer'
    },
    {
      id: 'chat-viewer-content',
      title: 'Chat Viewer area',
      description: 'All tools for browsing dialogs and saving labels live below. Select an integration on the Integrations tab first; otherwise you will see a short reminder here.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'chat-viewer'
    },
    {
      id: 'connector-chat-viewer-panel',
      title: 'Panel overview',
      description: 'When an integration is selected, use the filter and Refresh to load dialogs. Pick a dialog on the left to open the thread and human label forms on the right.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'chat-viewer'
    },
    {
      id: 'chat-viewer-layout',
      title: 'Dialogs and thread',
      description: 'The list on the left shows dialogs for the current filter. The thread on the right shows messages, resolved labels, and editors for human reactions, comments, and reference answers (when an integration is selected).',
      position: 'top',
      offset: { x: 0, y: -20 },
      tab: 'chat-viewer'
    }
  ];

  const ragDocumentsSteps: ConnectorOnboardingStep[] = [
    {
      id: 'rag-documents-tab',
      title: 'RAG Documents',
      description: 'Upload and organize documents used for RAG in your agents. Files are ingested per integration; pick an integration on Integrations before managing documents here.',
      position: 'bottom',
      offset: { x: 0, y: 20 },
      tab: 'rag-documents'
    },
    {
      id: 'rag-documents-tab-content',
      title: 'This tab\'s workspace',
      description: 'Without a selected integration you will see a prompt to go to Integrations. After you select one, folders, uploads, and the document list appear in this area.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'rag-documents'
    },
    {
      id: 'rag-documents-panel-anchor',
      title: 'Folders and documents',
      description: 'Create folders to group files, upload PDF/TXT/DOC/DOCX, then assign documents to folders and toggle usability once processing finishes. Documents can be linked from agent configurators via RAG document IDs.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'rag-documents'
    },
    {
      id: 'rag-documents-toolbar',
      title: 'Upload and filters',
      description: 'Use Upload to add files, then filter the document list by folder or status. This block appears after an integration is selected and the panel has loaded.',
      position: 'top',
      offset: { x: 0, y: -20 },
      tab: 'rag-documents'
    }
  ];

  const autoFineTuningSteps: ConnectorOnboardingStep[] = [
    {
      id: 'auto-fine-tuning-tab',
      title: 'Auto Fine-Tuning',
      description: 'Schedule recurring fine-tuning jobs from reactions and comments collected in your experiments. Each config ties agents, datasets, cadence, and training regime to your integration.',
      position: 'bottom',
      offset: { x: 0, y: 20 },
      tab: 'auto-fine-tuning'
    },
    {
      id: 'auto-fine-tuning-content',
      title: 'Configuration workspace',
      description: 'Create and edit auto fine-tuning configs here. Agent lists can be prefilled from the Integration Configurator when you open this tab from the same integration context.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'auto-fine-tuning'
    },
    {
      id: 'auto-fine-tuning-panel-root',
      title: 'Configs and runs',
      description: 'After you select an integration, use New Config to define schedule, period, history depth, and which agents are trainable. Existing configs can be enabled, edited, run on demand, or deleted.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'auto-fine-tuning'
    },
    {
      id: 'auto-ft-configs-list',
      title: 'Saved configs',
      description: 'Each card summarizes schedule, regime, last status, and next run. Use Run Now for a manual pass, or rely on the scheduler. This list is shown once configs exist for the integration.',
      position: 'top',
      offset: { x: 0, y: -20 },
      tab: 'auto-fine-tuning'
    }
  ];

  const pipelinesSteps: ConnectorOnboardingStep[] = [
    {
      id: 'pipelines-tab',
      title: 'Pipelines',
      description:
        'Compose scheduled automation flows that chain dataset loading, config tuning, and fine-tuning for your integration.',
      position: 'bottom',
      offset: { x: 0, y: 20 },
      tab: 'pipelines',
    },
    {
      id: 'pipelines-content',
      title: 'Pipelines workspace',
      description:
        'Select an integration on the Integrations tab first. Then create pipelines, run them on demand, and monitor active runs from this area.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'pipelines',
    },
    {
      id: 'pipeline-panel-toolbar',
      title: 'Create pipelines',
      description:
        'Use Full cycle to scaffold a complete flow (datasets → tuning → fine-tuning), or New pipeline to start from a blank template and pick steps yourself.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'pipelines',
    },
    {
      id: 'pipeline-panel-list',
      title: 'Your pipelines',
      description:
        'Saved pipelines appear here. Run now starts a pass immediately; Edit opens the builder; View run shows progress and task details for the latest execution.',
      position: 'top',
      offset: { x: 0, y: -20 },
      tab: 'pipelines',
    },
  ];

  const fineTuningGraphSteps: ConnectorOnboardingStep[] = [
    {
      id: 'fine-tuning-graph-tab',
      title: 'Fine Tuning Graph',
      description: 'Visualize how fine-tuned model aliases relate to base models for the selected integration. Useful for tracing which training jobs produced which deployable names.',
      position: 'bottom',
      offset: { x: 0, y: 20 },
      tab: 'fine-tuning-graph'
    },
    {
      id: 'fine-tuning-graph-content',
      title: 'Graph workspace',
      description: 'The graph loads for the integration currently selected on the Integrations tab. If none is selected, you will see a short instruction instead of the diagram.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'fine-tuning-graph'
    },
    {
      id: 'fine-tuning-graph-panel-root',
      title: 'Lineage view',
      description: 'Nodes represent models or aliases; edges show training lineage. Pan and zoom with the built-in controls, and use the minimap on large graphs.',
      position: 'left',
      offset: { x: -30, y: 0 },
      tab: 'fine-tuning-graph'
    },
    {
      id: 'fine-tuning-graph-container',
      title: 'Interactive canvas',
      description: 'When data exists, the React Flow canvas renders here. Empty or error states are shown in the same region if the backend returns no graph or a request fails.',
      position: 'top',
      offset: { x: 0, y: -20 },
      tab: 'fine-tuning-graph'
    }
  ];

  // Выбираем шаги в зависимости от активной вкладки
  const getStepsForTab = (tab: ConnectorTabType): ConnectorOnboardingStep[] => {
    switch (tab) {
      case 'integrations':
        return integrationsSteps;
      case 'virtual-users':
        return virtualUsersSteps;
      case 'datasets':
        return datasetsSteps;
      case 'chat-viewer':
        return chatViewerSteps;
      case 'rag-documents':
        return ragDocumentsSteps;
      case 'auto-fine-tuning':
        return autoFineTuningSteps;
      case 'pipelines':
        return pipelinesSteps;
      case 'fine-tuning-graph':
        return fineTuningGraphSteps;
      case 'workflow':
        // No guided tour for the workflow editor yet — the tour closes itself on empty steps.
        return [];
      default:
        return integrationsSteps;
    }
  };

  const steps = getStepsForTab(activeTab);

  useEffect(() => {
    if (isOpen && steps.length === 0) onClose();
  }, [isOpen, steps.length, onClose]);

  const tour = useGuidedTour({
    isOpen,
    steps,
    onClose,
    layout: 'center',
    resetKey: activeTab,
    onStepEnter: async (step) => {
      if (
        step.id === 'connector-config-section' &&
        onSelectIntegration &&
        integrations &&
        integrations.length > 0 &&
        !selectedIntegrationId
      ) {
        const firstIntegration = integrations[0];
        const integrationId = String(firstIntegration?.id || firstIntegration?._id || '');
        if (integrationId) {
          onSelectIntegration(integrationId);
          await new Promise((resolve) => window.setTimeout(resolve, 500));
        }
      }
    },
  });

  if (!isOpen || !tour.currentStepData) return null;

  const tourTitles: Partial<Record<ConnectorTabType, string>> = {
    integrations: 'Connector — Integrations',
    'virtual-users': 'Connector — Virtual Users',
    datasets: 'Connector — Datasets',
    'chat-viewer': 'Connector — Chat Viewer',
    'rag-documents': 'Connector — RAG Documents',
    'auto-fine-tuning': 'Connector — Auto Fine-Tuning',
    pipelines: 'Connector — Pipelines',
    'fine-tuning-graph': 'Connector — Fine Tuning Graph',
    workflow: 'Connector — Workflow',
  };

  return (
    <GuidedTourOverlay
      isOpen={isOpen}
      tourTitle={tourTitles[activeTab] ?? 'Connector guided tour'}
      stepsCount={tour.stepCount}
      currentStep={tour.safeStepIndex}
      stepTitle={tour.currentStepData.title}
      stepDescription={tour.currentStepData.description}
      contextLabel={tour.currentStepData.contextLabel}
      tooltipX={tour.tooltipPosition.x}
      tooltipY={tour.tooltipPosition.y}
      position={tour.currentPosition}
      onClose={tour.handleSkip}
      onNext={tour.handleNext}
      onPrev={tour.handlePrev}
    />
  );
};

export default ConnectorOnboarding;
